import * as echarts from "echarts";
import { datetime } from "../../../util/library/datetime";
import { getStore } from "../../../util/store/store";
import { OPERATION_TYPE, SensorGroupType } from "../../../util/varibles/constants";
import { IOperation2, ISensorItem, ITankProcessData } from "../../../util/varibles/interface";
import { selectOpDetail } from "../constants";

export type SensorItemStyle = {
    color?: string;
    borderColor?: string;
    borderWidth?: number;
};

export type SensorSeriesStyle = {
    color: string;
    symbol: string;
    lineType: 'solid' | 'dashed';
    itemStyle: SensorItemStyle;
    areaStyle?: object;
    stack?: string;
};

export type SensorSeriesRenderer = (
    data: number[][],
    unit?: string,
    sensor?: ISensorItem
) => object[];

export const sensorConfig: Record<SensorGroupType, {
    color?: string;
    yAxisId: string;
    symbol: string;
    showSymbol: boolean;
    lineType?: 'solid' | 'dashed';
    symbolSize?: number | ((value: [number, number]) => number);
    areaStyle?: object;
    itemStyle?: SensorItemStyle;
}> = {
    oxygen: {
        color: "#92B6D5",
        yAxisId: "percent",
        symbol: "circle",
        showSymbol: true,
        symbolSize: 8,
        itemStyle: {
            borderWidth: 2,
            borderColor: "#92B6D5",
            color: "#ffffff"
        }
    },
    temperature: {
        color: "#f4a261",
        yAxisId: "temperature",
        symbol: "none",
        showSymbol: false,
        lineType: "dashed"
    },
    cargoPressure: {
        yAxisId: "bar",
        symbol: "circle",
        showSymbol: true,
    },
    co2: {
        color: "#5470C6",
        yAxisId: "percent",
        symbol: "triangle",
        symbolSize: 20,
        showSymbol: true,
        lineType: "dashed",
        itemStyle: {
            borderWidth: 3,
            borderColor: "#EE6666",
            color: "yellow"
        }
    },
    ph: {
        color: "#91cc75",
        yAxisId: "ph",
        symbol: "none",
        showSymbol: false
    }
};

const baseLineSeries = (data: number[][], unit: string | undefined, sensor: ISensorItem) => {
    const config = sensorConfig[sensor.type];

    return {
        name: sensor.name,
        type: "line",
        smooth: true,
        z: 6,
        yAxisId: config.yAxisId,
        symbol: config.symbol,
        symbolSize: config.symbolSize || 7,
        showSymbol: config.showSymbol,
        lineStyle: {
            color: config.color,
            width: 2,
            type: config.lineType || "solid"
        },
        itemStyle: config.itemStyle,
        data,
        unit
    };
};

export const sensorSeriesRenderers: Record<string, SensorSeriesRenderer> = {
    oxygen: (data, unit, sensor) => sensor ? [baseLineSeries(data, unit, sensor)] : [],
    temperature: (data, unit, sensor) => sensor ? [baseLineSeries(data, unit, sensor)] : [],
    co2: (data, unit, sensor) => sensor ? [baseLineSeries(data, unit, sensor)] : [],
    ph: (data, unit, sensor) => sensor ? [baseLineSeries(data, unit, sensor)] : [],
    cargoPressure: (data, unit, sensor) => {
        if (!sensor) return [];
        const isTop = sensor.name.toLowerCase().includes("top");
        const color = isTop ? "#F2597F" : "#0770FF";
        const areaColors = isTop
            ? ['rgba(213,72,120,0.8)', 'rgba(213,72,120,0.3)']
            : ['rgba(58,77,233,0.8)', 'rgba(58,77,233,0.3)'];
        const config = sensorConfig[sensor.type];
        return [{
            name: sensor.name,
            type: 'line',
            smooth: true,
            z: 5,
            yAxisId: config.yAxisId,
            symbol: config.symbol,
            symbolSize: 5,
            itemStyle: { color },
            sampling: 'average',
            stack: 'a',
            areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: areaColors[0] },
                    { offset: 1, color: areaColors[1] }
                ])
            },
            data,
            unit
        }]
    }
};

export const UNIT_AXES = [
    {
        id: "bar",
        name: "bar",
        color: "#0770FF",
        offset: 110,
    },
    {
        id: "percent",
        name: "%",
        color: "#5470c6",
        showSplitLine: true,
        offset: 25,
    },
    {
        id: "temperature",
        name: "°C",
        color: "#f4a261",
        offset: 70,
    },
    {
        id: "ph",
        name: "pH",
        color: "#91cc75",
        offset: 160,
    },
] as const;

export const getTimelineFromActivityLog = (value?: IOperation2) => {
    const op = value || selectOpDetail.data(getStore());
    const { operation, activity_log: source = [] } = op || {};

    switch (operation?.operation_type) {
        case OPERATION_TYPE.HARVEST: {
            const finish = Math.max(source[source.length - 1]?.real_start_time || Date.now(), source[source.length - 2]?.real_finish_time || 0);
            return { start: source[0].real_start_time, finish: datetime(finish).endOf('minute').time }
        }
        case OPERATION_TYPE.TREATMENT: {
            const { real_round_map } = operation;
            let maxTime = 0;

            Object.values(real_round_map || {}).forEach(round => {
                maxTime = Math.max(
                    maxTime,
                    round.real_finish_time || 0,
                    round.real_start_time || 0,
                );

                round.tanks.forEach(tank => {
                    maxTime = Math.max(
                        maxTime,
                        tank.start_time || 0,
                        tank.finish_time || 0
                    );

                    tank.processes?.forEach(process => {
                        maxTime = Math.max(
                            maxTime,
                            process.real_start_time || 0,
                            process.finish_time || 0,
                            process.start_time || 0
                        );
                    });
                });

                round.vessel_movements?.forEach(item => {
                    maxTime = Math.max(maxTime, item.start_time);
                });
            });
            const finish = Math.max(source[source.length - 1]?.real_start_time || Date.now(), maxTime);
            return { start: source[0].real_start_time, finish: datetime(finish).endOf('minute').time }
        }
        default:
            return { start: 0, finish: 0 }

    }
}

const mergeArrayData = (
    oldValue: ISensorItem["data"],
    newValue: ISensorItem["data"]
): ISensorItem["data"] => {
    if (oldValue.length === 0) return newValue;
    if (newValue.length === 0) return oldValue;

    type TSensorPoint =
        NonNullable<ISensorItem["data"]>[number];

    const isSamePoint = (pointA: TSensorPoint, pointB: TSensorPoint) => {
        if (pointA.timestamp !== undefined && pointB.timestamp !== undefined) {
            return pointA.timestamp === pointB.timestamp;
        }

        return false;
    };

    const lastOldItem = oldValue[oldValue.length - 1];
    const firstNewItem = newValue[0];

    if (isSamePoint(lastOldItem, firstNewItem)) {
        return [...oldValue, ...newValue.slice(1),];
    }

    return [...oldValue, ...newValue,];
};

export const mergeSensorData = (oldData: Record<string, ITankProcessData>, newData: Record<string, ITankProcessData>,): Record<string, ITankProcessData> => {
    const result = { ...oldData };

    Object.entries(newData).forEach(
        ([tankName, newTank]) => {
            const oldTank = oldData[tankName] || {};

            result[tankName] = Object.entries(newTank).reduce(
                (tank, [key, newSensors]) => {
                    const typedKey = key as keyof ITankProcessData;

                    const oldSensors = oldTank[typedKey];

                    if (!Array.isArray(newSensors)) {
                        tank[typedKey] = newSensors;
                        return tank;
                    }

                    const mergedSensors = Array.isArray(oldSensors)
                        ? oldSensors.map(
                            (oldSensor, index) => {
                                const newSensor = newSensors[index];
                                if (!newSensor) {
                                    return oldSensor;
                                }

                                const newValue = newSensor.data || [];


                                return {
                                    ...oldSensor,
                                    ...newSensor,
                                    data: mergeArrayData(oldSensor.data || [], newValue),
                                };
                            }
                        )
                        : newSensors;

                    tank[typedKey] = mergedSensors as never;

                    return tank;
                }, { ...oldTank, } as ITankProcessData
            );
        }
    );

    return result;
};